import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ActionDetail, ContactPickItem } from '@tww/shared';
import { ACTION_CONTACT_ROLE_LABELS, ACTION_CONTACT_STATUS_LABELS } from '@tww/shared';
import { api, ApiRequestError } from '../../api/client';
import { ContactPicker } from '../../ui/ContactPicker';
import { ErrorState, Skeleton } from '../../ui/basics';
import { Select, TextArea } from '../../ui/fields';
import { useToast } from '../../ui/Toast';

/** Beteiligung an einer Aktion hinzufügen (Tab „Beteiligte“): eigene Seite statt Modal */
export function ActionContactFormPage() {
  const { id } = useParams();
  const aid = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['action', aid], queryFn: () => api.get<ActionDetail>(`/actions/${aid}`) });
  const [role, setRole] = useState('');
  const [contact, setContact] = useState<ContactPickItem | null>(null);
  const [status, setStatus] = useState('geplant');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const back = `/aktionen/${aid}?tab=beteiligte`;
  const save = useMutation({
    mutationFn: () => api.post(`/actions/${aid}/contacts`, { contact_id: contact!.id, role, status, note: note.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['action', aid] });
      toast('Beteiligung gespeichert');
      nav(back);
    },
    onError: (e: Error) => {
      if (e instanceof ApiRequestError && e.fields) setErrors(e.fields);
      toast(e.message, 'error');
    },
  });
  if (q.isLoading) return <Skeleton rows={6} />;
  if (q.isError || !q.data) return <ErrorState error={q.error ?? new Error('Aktion nicht gefunden')} retry={() => q.refetch()} />;
  const a = q.data;
  const missing = [!role && 'Rolle', !contact && 'Kontakt'].filter(Boolean) as string[];
  return (
    <>
      <div className="crumb">
        <Link to="/aktionen">Aktionen</Link> › <Link to={back}>{a.label}</Link>
      </div>
      <div className="head">
        <div>
          <h1>Beteiligung hinzufügen</h1>
          <div className="sub">{a.code}</div>
        </div>
      </div>
      <div className="page-form">
        <div className="section">
          <div className="sh">
            <h2>Beteiligung</h2>
          </div>
          <div className="sb">
            <div className="form">
              <Select label="Rolle" required value={role} onChange={setRole} options={Object.entries(ACTION_CONTACT_ROLE_LABELS).filter(([k]) => k !== 'partner').map(([k, l]) => ({ value: k, label: l }))} placeholder="wählen" error={errors.role} />
              <ContactPicker label="Kontakt" required value={contact} onChange={setContact} region={a.region ?? undefined} error={errors.contact_id} />
              <Select label="Stand" value={status} onChange={(v) => setStatus(v || 'geplant')} options={(['geplant', 'angefragt', 'bestaetigt', 'gebucht'] as const).map((v) => ({ value: v, label: ACTION_CONTACT_STATUS_LABELS[v] }))} error={errors.status} />
              <TextArea label="Notiz" value={note} onChange={setNote} rows={2} error={errors.note} />
            </div>
          </div>
        </div>
        <div className="aside">
          <div className="section">
            <div className="sb summary-box">
              <strong>{a.label}</strong>
              <dl>
                <dt>Rolle</dt>
                <dd>{role ? ACTION_CONTACT_ROLE_LABELS[role as keyof typeof ACTION_CONTACT_ROLE_LABELS] : '–'}</dd>
                <dt>Kontakt</dt>
                <dd>{contact?.display_name ?? '–'}</dd>
                <dt>Stand</dt>
                <dd>{ACTION_CONTACT_STATUS_LABELS[status as keyof typeof ACTION_CONTACT_STATUS_LABELS] ?? status}</dd>
              </dl>
            </div>
          </div>
          <div className="actions-col">
            <button type="button" className="btn primary" disabled={missing.length > 0 || save.isPending} onClick={() => save.mutate()}>
              Speichern
            </button>
            {missing.length > 0 && <span className="mut small">Es fehlt: {missing.join(', ')}</span>}
            <Link className="btn" to={back}>
              Abbrechen
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
